// frontend/src/components/CarbonCredit/Calculator.tsx
import React, { useState } from 'react';

const CarbonCreditCalculator: React.FC = () => {
  const [treeCount, setTreeCount] = useState<number>(0);
  
  // Average carbon sequestration per tree per year (in kg CO2)
  const CARBON_SEQUESTRATION_PER_TREE = 21.77; // kg CO2 per year
  
  const calculateCarbonOffset = (trees: number): number => {
    return trees * CARBON_SEQUESTRATION_PER_TREE;
  };

  const formatCarbon = (kg: number): string => {
    if (kg >= 1000) {
      return `${(kg / 1000).toFixed(2)} tons`;
    }
    return `${kg.toFixed(2)} kg`;
  };

  const carbonOffset = calculateCarbonOffset(treeCount);

  return (
    <div className="card shadow-lg">
      <div className="card-header bg-success text-white">
        <h4 className="mb-0">Carbon Offset Calculator</h4>
      </div>
      <div className="card-body">
        <div className="row align-items-center">
          <div className="col-md-6">
            <div className="mb-4">
              <label htmlFor="treeCount" className="form-label h5">
                Number of Trees:
              </label>
              <input
                type="range"
                className="form-range"
                id="treeCount"
                min="0"
                max="1000"
                step="1"
                value={treeCount}
                onChange={(e) => setTreeCount(parseInt(e.target.value))}
              />
              <div className="d-flex justify-content-between">
                <span>0</span>
                <span className="fw-bold">{treeCount} trees</span>
                <span>1000</span>
              </div>
            </div>
            
            <div className="input-group mb-3">
              <input
                type="number"
                className="form-control form-control-lg"
                value={treeCount}
                onChange={(e) => setTreeCount(parseInt(e.target.value) || 0)}
                min="0"
                max="10000"
              />
              <span className="input-group-text">trees</span>
            </div>
          </div>
          
          <div className="col-md-6 text-center">
            <div className="carbon-result p-4 bg-light rounded">
              <h5 className="text-success">Annual Carbon Offset</h5>
              <div className="display-4 fw-bold text-success mb-2">
                {formatCarbon(carbonOffset)}
              </div>
              <small className="text-muted">of CO₂ sequestered per year</small>
              
              <div className="mt-3">
                <div className="progress" style={{ height: '20px' }}>
                  <div
                    className="progress-bar bg-success"
                    role="progressbar"
                    style={{ width: `${Math.min((treeCount / 100) * 100, 100)}%` }}
                    aria-valuenow={treeCount}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  >
                    {treeCount >= 10 && `${treeCount} trees`}
                  </div>
                </div>
                <small className="text-muted">
                  Equivalent to {((carbonOffset / 21000) * 100).toFixed(1)}% of an average person's annual carbon footprint
                </small>
              </div>
            </div>
          </div>
        </div>
        
        <div className="row mt-4">
          <div className="col-12">
            <div className="alert alert-info">
              <h6>💡 Did You Know?</h6>
              <p className="mb-0">
                A single mature tree can absorb approximately {CARBON_SEQUESTRATION_PER_TREE} kg of CO₂ per year. 
                By planting {treeCount} trees, you're helping to create a sustainable future while contributing to 
                climate resilience in African communities.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CarbonCreditCalculator;